import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Edit, Trash2, Upload, Download, Truck, Users, X,
  CheckCircle, AlertCircle, UserPlus, Search, ArrowLeft,
  ArrowRight, Link2, Sparkles, Phone, Hash, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSession } from '../../contexts/SessionContext';
import RightSlideModal from '../../components/RightSlideModal';
import CommonTable, { TableRowAvatarCell } from '../../components/CommonTable';

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  name: '', rollNumber: '', classId: '', sectionId: '',
  admissionNumber: '', dateOfBirth: '',
  fatherName: '', fatherPhone: '', motherName: '', motherPhone: '',
  aadharNumber: '',
  address: '', familyId: '', useTransport: false, routeId: '',
  pickupPoint: '', siblingDiscountApplied: false, scholarshipPercent: 0,
};

// ─── CSV Schema Fields ────────────────────────────────────────────────────────

// Base schema — required overridden at runtime by school field settings
const SCHEMA_FIELDS_BASE = [
  { key: 'name',                   label: 'Student Name',            required: true,  hint: 'Full name' },
  { key: 'rollNumber',             label: 'Roll Number',             required: false, hint: 'e.g. 101' },
  { key: 'className',              label: 'Class Name',              required: true,  hint: 'Must match an existing class' },
  { key: 'sectionName',            label: 'Section Name',            required: true,  hint: 'Must match a section in that class' },
  { key: 'admissionNumber',        label: 'Admission Number',        required: true,  hint: 'Unique — e.g. ADM2024001' },
  { key: 'dateOfBirth',            label: 'Date of Birth',           required: false, hint: 'YYYY-MM-DD' },
  { key: 'fatherName',             label: 'Father Name',             required: true,  hint: "Father's full name" },
  { key: 'fatherPhone',            label: 'Father Phone',            required: true,  hint: '10-digit number' },
  { key: 'motherName',             label: 'Mother Name',             required: false, hint: "Mother's full name" },
  { key: 'motherPhone',            label: 'Mother Phone',            required: false, hint: '10-digit number' },
  { key: 'aadharNumber',           label: 'Aadhar Number',           required: false, hint: '12-digit Aadhar' },
  { key: 'address',                label: 'Address',                 required: false, hint: 'Street / locality' },
  { key: 'familyId',               label: 'Family ID',               required: false, hint: 'Siblings share same code — FAM-001' },
  { key: 'useTransport',           label: 'Use Transport',           required: false, hint: 'yes / no' },
  { key: 'transportRouteCode',     label: 'Transport Route Code',    required: false, hint: 'Route code — e.g. R-NORTH' },
  { key: 'pickupPoint',            label: 'Pickup Point',            required: false, hint: 'Stop name' },
  { key: 'siblingDiscountApplied', label: 'Sibling Discount',        required: false, hint: 'yes / no' },
  { key: 'scholarshipPercent',     label: 'Scholarship %',           required: false, hint: 'Number 0–100' },
];

// Keys whose required status can be overridden by school settings
const TOGGLEABLE_KEYS = [
  'rollNumber', 'admissionNumber', 'dateOfBirth',
  'fatherName', 'fatherPhone', 'motherName', 'motherPhone',
  'aadharNumber', 'address', 'familyId',
];

function applyFieldSettings(settings) {
  return SCHEMA_FIELDS_BASE.map(f =>
    TOGGLEABLE_KEYS.includes(f.key) ? { ...f, required: !!settings[f.key] } : f
  );
}

// Aliases for auto-detecting which CSV column maps to which schema field
const FIELD_ALIASES = {
  name:                   ['name', 'studentname', 'fullname', 'student'],
  rollNumber:             ['rollnumber', 'roll', 'rollno', 'srno'],
  className:              ['classname', 'class', 'grade'],
  sectionName:            ['sectionname', 'section', 'div', 'division'],
  admissionNumber:        ['admissionnumber', 'admno', 'admission', 'adm'],
  dateOfBirth:            ['dateofbirth', 'dob', 'birthdate', 'birthday'],
  fatherName:             ['fathername', 'fathersfullname', 'parentname', 'guardian'],
  fatherPhone:            ['fatherphone', 'fathermobile', 'parentphone', 'phone', 'mobile', 'contact'],
  motherName:             ['mothername', 'mothersfullname'],
  motherPhone:            ['motherphone', 'mothermobile'],
  aadharNumber:           ['aadharnumber', 'aadhar', 'aadhaar', 'uidai', 'aadharno'],
  address:                ['address', 'addr'],
  familyId:               ['familyid', 'familycode', 'family'],
  useTransport:           ['usetransport', 'transport', 'bus'],
  transportRouteCode:     ['transportroutecode', 'routecode', 'route'],
  pickupPoint:            ['pickuppoint', 'pickup', 'stop', 'busstop'],
  siblingDiscountApplied: ['siblingdiscountapplied', 'siblingdiscount', 'sibling'],
  scholarshipPercent:     ['scholarshippercent', 'scholarship', 'schpercent'],
};

function normalizeKey(s) {
  return s.toLowerCase().replace(/[\s_\-\.]+/g, '');
}

function autoDetectMapping(headers) {
  const mapping = {};
  headers.forEach(header => {
    const norm = normalizeKey(header);
    for (const [fieldKey, aliases] of Object.entries(FIELD_ALIASES)) {
      if (!mapping[fieldKey] && aliases.includes(norm)) {
        mapping[fieldKey] = header;
      }
    }
  });
  return mapping;
}

function parseCSVText(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 1) return { headers: [], rows: [] };
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const row = {};
    headers.forEach((h, idx) => { row[h] = values[idx] ?? ''; });
    rows.push(row);
  }
  return { headers, rows };
}

// ─── Family code generator ────────────────────────────────────────────────────

function generateFamilyCode(existingStudents) {
  const used = new Set(existingStudents.map(s => s.familyId).filter(Boolean));
  let code;
  do {
    code = `FAM-${Math.floor(1000 + Math.random() * 9000)}`;
  } while (used.has(code));
  return code;
}

// ─── Step: Sibling Check (Step 1) ─────────────────────────────────────────────

function SiblingCheckStep({ onNo, onYes }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[420px] px-4 py-8 text-center">
      {/* Icon */}
      <div className="w-14 h-14 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center mb-5">
        <Sparkles className="w-7 h-7 text-indigo-500" />
      </div>

      <h3 className="text-xl font-bold text-slate-800 mb-2">Smart Admission</h3>
      <p className="text-sm text-slate-500 max-w-xs leading-relaxed mb-8">
        Is this student a sibling of someone already enrolled in this school?
      </p>

      {/* Option Cards */}
      <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
        {/* No Card */}
        <button
          onClick={onNo}
          className="group flex flex-col items-center gap-3 p-5 rounded-2xl border-2 border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 transition-all text-left"
        >
          <div className="w-11 h-11 rounded-xl bg-slate-100 group-hover:bg-indigo-100 flex items-center justify-center transition-colors">
            <UserPlus className="w-5 h-5 text-slate-500 group-hover:text-indigo-600 transition-colors" />
          </div>
          <div>
            <p className="font-bold text-sm text-slate-800">New Family</p>
            <p className="text-[11px] text-slate-400 mt-0.5 leading-tight">First child from this family</p>
          </div>
        </button>

        {/* Yes Card */}
        <button
          onClick={onYes}
          className="group flex flex-col items-center gap-3 p-5 rounded-2xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/40 transition-all text-left"
        >
          <div className="w-11 h-11 rounded-xl bg-slate-100 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
            <Users className="w-5 h-5 text-slate-500 group-hover:text-emerald-600 transition-colors" />
          </div>
          <div>
            <p className="font-bold text-sm text-slate-800">Has Sibling</p>
            <p className="text-[11px] text-slate-400 mt-0.5 leading-tight">Already has a sibling enrolled</p>
          </div>
        </button>
      </div>

      <p className="text-[10px] text-slate-300 mt-6">
        Siblings are auto-linked to the same Family ID for discount tracking
      </p>
    </div>
  );
}

// ─── Step: Search Sibling (Step 2) ───────────────────────────────────────────

function SearchSiblingStep({ students, classes, sections, onBack, onSelect }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const results = query.trim().length > 0
    ? students.filter(s => {
        const q = query.toLowerCase();
        return (
          s.name?.toLowerCase().includes(q) ||
          s.fatherPhone?.includes(q) ||
          s.motherPhone?.includes(q) ||
          s.familyId?.toLowerCase().includes(q) ||
          s.fatherName?.toLowerCase().includes(q) ||
          s.motherName?.toLowerCase().includes(q)
        );
      }).slice(0, 8)
    : [];

  // Group selected family's members
  const familyMembers = selected
    ? students.filter(s => s.familyId === selected.familyId && s.familyId)
    : [];

  const getClassSection = (s) => {
    const cls = classes.find(c => c.id === s.classId);
    const sec = sections.find(sec => sec.id === s.sectionId);
    return [cls?.className, sec?.sectionName].filter(Boolean).join(' - ') || 'N/A';
  };

  return (
    <div className="flex flex-col min-h-[420px]">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
          <ArrowLeft className="w-4 h-4 text-slate-500" />
        </button>
        <div>
          <p className="font-bold text-slate-800 text-sm">Find Existing Family</p>
          <p className="text-[11px] text-slate-400">Search by name, phone number, or Family ID</p>
        </div>
      </div>

      {/* Search Input */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setSelected(null); }}
          placeholder="Name, phone number, Family ID…"
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 text-sm outline-none transition-all"
        />
      </div>

      {/* Search Tips (empty state) */}
      {query.trim() === '' && !selected && (
        <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-400 py-6">
          <div className="grid grid-cols-3 gap-3 text-[10px] font-semibold text-slate-400 mb-4 w-full max-w-xs">
            {[
              { icon: Users, text: 'Parent Name' },
              { icon: Phone, text: 'Parent Phone' },
              { icon: Hash, text: 'Family Code' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex flex-col items-center gap-1.5 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <Icon className="w-4 h-4" />
                {text}
              </div>
            ))}
          </div>
          <p className="text-[11px]">Start typing to search enrolled students</p>
        </div>
      )}

      {/* Results List */}
      {query.trim() !== '' && !selected && (
        <div className="flex-1 space-y-2 overflow-y-auto">
          {results.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">
              No students found matching "<span className="font-semibold">{query}</span>"
            </div>
          ) : results.map(student => (
            <button
              key={student.id}
              onClick={() => setSelected(student)}
              className="w-full text-left p-3 rounded-xl border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50/30 bg-white transition-all group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm text-slate-800">{student.name}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {getClassSection(student)}
                    {student.familyId && <> · <span className="font-mono font-bold text-indigo-600">{student.familyId}</span></>}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-slate-500 font-medium">{student.fatherName || student.motherName}</p>
                  <p className="text-[11px] text-slate-400">{student.fatherPhone || student.motherPhone}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Selected family confirmation */}
      {selected && (
        <div className="flex-1 space-y-4">
          {/* Selected Family Card */}
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Family Found</span>
              <button onClick={() => setSelected(null)} className="ml-auto text-emerald-500 hover:text-emerald-700">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 font-medium">Family Code</p>
                <p className="font-mono font-black text-lg text-emerald-800">{selected.familyId || 'No code'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-emerald-600 font-medium">Father</p>
                <p className="text-sm font-semibold text-emerald-800">{selected.fatherName}</p>
                <p className="text-xs text-emerald-600">{selected.fatherPhone}</p>
              </div>
            </div>
          </div>

          {/* Siblings in this family */}
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
              {familyMembers.length} Sibling{familyMembers.length !== 1 ? 's' : ''} in this family
            </p>
            <div className="space-y-1.5">
              {familyMembers.map(m => (
                <div key={m.id} className={`flex items-center justify-between px-3 py-2 rounded-xl border text-xs ${m.id === selected.id ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50/70 border-slate-100'}`}>
                  <span className="font-semibold text-slate-700">{m.name}</span>
                  <span className="text-slate-400">{getClassSection(m)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-700 font-medium">
              Discount will be assigned automatically by the <span className="font-bold">Fee Workflow</span> based on class.
            </p>
          </div>

          {/* Continue Button */}
          <button
            onClick={() => onSelect(selected)}
            className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold text-sm transition-all shadow-sm"
          >
            <Link2 className="w-4 h-4" />
            Link to this family & continue
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

// ─── Column definitions ───────────────────────────────────────────────────────

const DEFAULT_COLUMN_SETTINGS = [
  { key: 'student',      visible: true  },
  { key: 'admissionNo',  visible: false },
  { key: 'rollNo',       visible: false },
  { key: 'classSec',     visible: true  },
  { key: 'dateOfBirth',  visible: false },
  { key: 'fatherName',   visible: false },
  { key: 'fatherPhone',  visible: false },
  { key: 'motherName',   visible: false },
  { key: 'motherPhone',  visible: false },
  { key: 'aadharNumber', visible: false },
  { key: 'address',      visible: false },
  { key: 'familyId',     visible: false },
  { key: 'transport',    visible: false },
  { key: 'credentials',  visible: false },
];

const COLUMN_HEADERS = {
  student:      'Student',
  admissionNo:  'Admission No.',
  rollNo:       'Roll No.',
  classSec:     'Class & Section',
  dateOfBirth:  'Date of Birth',
  fatherName:   'Father Name',
  fatherPhone:  'Father Phone',
  motherName:   'Mother Name',
  motherPhone:  'Mother Phone',
  aadharNumber: 'Aadhar No.',
  address:      'Address',
  familyId:     'Family ID',
  transport:    'Transport',
  credentials:  'Login Credentials',
};

export default function StudentsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Search + pagination
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const searchRef = useRef(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [admissionStep, setAdmissionStep] = useState('check'); // 'check' | 'search' | 'form'
  const [linkedFamily, setLinkedFamily] = useState(null); // { familyId, parentName, parentPhone, siblingName }

  // Edit flow (separate from add flow)
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [availableSections, setAvailableSections] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);

  // Import CSV
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importStep, setImportStep] = useState('upload'); // 'upload' | 'map' | 'validate'
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [validating, setValidating] = useState(false);
  const [validateResult, setValidateResult] = useState(null);
  const fileInputRef = useRef(null);

  const { selectedSession, sessions } = useSession();
  const [modalSession, setModalSession] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');

  // Debounce search → reset page when filter changes
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, selectedClass, selectedSession]);

  // ── TanStack Query: support data (classes, sections, routes, settings) ───
  const supportQuery = useQuery({
    queryKey: ['students-support'],
    queryFn: async () => {
      const [classesRes, sectionsRes, routesRes, fieldSettingsRes, colSettingsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/sections'),
        api.get('/transport-routes'),
        api.get('/student-field-settings').catch(() => ({ data: null })),
        api.get('/student-column-settings').catch(() => ({ data: null })),
      ]);
      return {
        classes: classesRes.data,
        sections: sectionsRes.data,
        routes: routesRes.data,
        fieldSettings: fieldSettingsRes.data,
        columnSettings: colSettingsRes.data,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 min — rarely changes
  });

  const classes = supportQuery.data?.classes ?? [];
  const sections = supportQuery.data?.sections ?? [];
  const transportRoutes = supportQuery.data?.routes ?? [];
  const fieldSettings = supportQuery.data?.fieldSettings ?? {
    rollNumber: false, admissionNumber: true, dateOfBirth: false,
    fatherName: true, fatherPhone: true, motherName: false, motherPhone: false,
    aadharNumber: false, address: false, familyId: false,
  };
  const columnSettings = useMemo(() => {
    const saved = supportQuery.data?.columnSettings;
    if (!saved) return DEFAULT_COLUMN_SETTINGS;
    const validKeys = new Set(DEFAULT_COLUMN_SETTINGS.map(d => d.key));
    const filtered = saved.filter(c => validKeys.has(c.key));
    const filteredKeys = new Set(filtered.map(c => c.key));
    const appended = DEFAULT_COLUMN_SETTINGS.filter(d => !filteredKeys.has(d.key));
    return [...filtered, ...appended];
  }, [supportQuery.data?.columnSettings]);

  // ── TanStack Query: paginated students (cached per page/search/filter) ───
  const studentsQuery = useQuery({
    queryKey: ['students', { page, search: debouncedSearch, classId: selectedClass, session: selectedSession }],
    queryFn: async () => {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (selectedClass !== 'all') params.set('classId', selectedClass);
      const res = await api.get(`/students?${params}`);
      return res.data;
    },
    staleTime: 30 * 1000, // 30 s — navigating back to same page hits cache
    placeholderData: keepPreviousData, // show old data while next page loads
  });

  const students = studentsQuery.data?.students ?? [];
  const totalStudents = studentsQuery.data?.total ?? 0;
  const totalPages = Math.ceil(totalStudents / PAGE_SIZE);
  const loading = supportQuery.isLoading;
  const fetching = studentsQuery.isFetching;

  const invalidateStudents = () =>
    queryClient.invalidateQueries({ queryKey: ['students'] });

  const filteredStudents = students; // already filtered server-side

  const fetchSectionsForForm = async (classId) => {
    try {
      const res = await api.get(`/sections?classId=${classId}`);
      setAvailableSections(res.data);
    } catch {
      toast.error('Failed to fetch sections');
    }
  };

  // ── Open Add Modal (always starts at 'check' step) ──────────────────────

  const openAddModal = () => {
    setSelectedStudent(null);
    setFormData(EMPTY_FORM);
    setLinkedFamily(null);
    setAdmissionStep('check');
    setModalSession(selectedSession === 'all' ? '' : selectedSession);
    setAvailableSections([]);
    setShowModal(true);
  };

  // ── Open Edit Modal (skips smart flow) ──────────────────────────────────

  const openEditModal = (student) => {
    setSelectedStudent(student);
    setFormData({
      name: student.name || '',
      rollNumber: student.rollNumber || '',
      classId: student.classId || '',
      sectionId: student.sectionId || '',
      admissionNumber: student.admissionNumber || '',
      dateOfBirth: student.dateOfBirth || '',
      fatherName: student.fatherName || '',
      fatherPhone: student.fatherPhone || '',
      motherName: student.motherName || '',
      motherPhone: student.motherPhone || '',
      aadharNumber: student.aadharNumber || '',
      address: student.address || '',
      familyId: student.familyId || '',
      useTransport: student.useTransport || false,
      routeId: student.routeId || '',
      pickupPoint: student.pickupPoint || '',
      siblingDiscountApplied: student.siblingDiscountApplied || false,
      scholarshipPercent: student.scholarshipPercent || 0,
    });
    setLinkedFamily(null);
    fetchSectionsForForm(student.classId);
    const cls = classes.find(c => c.id === student.classId);
    setModalSession(cls?.sessionYear || '');
    setAdmissionStep('form'); // edit always goes straight to form
    setShowModal(true);
  };

  // ── Smart Admission Handlers ─────────────────────────────────────────────

  const handleNewFamily = () => {
    const code = generateFamilyCode(students);
    setLinkedFamily(null);
    setFormData(f => ({ ...f, familyId: code, siblingDiscountApplied: false }));
    setAdmissionStep('form');
  };

  const handleFindSibling = () => {
    setAdmissionStep('search');
  };

  const handleSiblingSelected = (sibling) => {
    const familyData = {
      familyId: sibling.familyId,
      fatherName: sibling.fatherName,
      fatherPhone: sibling.fatherPhone,
      motherName: sibling.motherName,
      motherPhone: sibling.motherPhone,
      siblingName: sibling.name,
    };
    setLinkedFamily(familyData);
    setFormData(f => ({
      ...f,
      familyId: sibling.familyId || '',
      fatherName: sibling.fatherName || '',
      fatherPhone: sibling.fatherPhone || '',
      motherName: sibling.motherName || '',
      motherPhone: sibling.motherPhone || '',
    }));
    setAdmissionStep('form');
  };

  // ── Form Submit ──────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedStudent) {
        await api.put(`/students/${selectedStudent.id}`, formData);
        toast.success('Student updated');
      } else {
        await api.post('/students', formData);
        toast.success('Student added' + (linkedFamily ? ` and linked to ${linkedFamily.familyId}` : ''));
      }
      setShowModal(false);
      invalidateStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save student');
    }
  };

  const handleDelete = async (studentId) => {
    if (!window.confirm('Delete this student?')) return;
    try {
      await api.delete(`/students/${studentId}`);
      toast.success('Student deleted');
      invalidateStudents();
    } catch {
      toast.error('Failed to delete student');
    }
  };

  // ── CSV Import ───────────────────────────────────────────────────────────

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/students/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.setAttribute('download', 'student_import_template.csv');
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    setImportFile(file);
    setImportResult(null);
    setValidateResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCSVText(e.target.result);
      setCsvHeaders(headers);
      setCsvRows(rows);
      setColumnMapping(autoDetectMapping(headers));
      setImportStep('map');
    };
    reader.readAsText(file);
  };

  const buildRemappedCSV = () => {
    const header = schemaFields.map(f => f.key).join(',');
    const rowLines = csvRows.map(row =>
      schemaFields.map(f => {
        const csvCol = columnMapping[f.key];
        return csvCol ? (row[csvCol] ?? '') : '';
      }).join(',')
    );
    return header + '\n' + rowLines.join('\n');
  };

  const handleValidate = async () => {
    if (!csvRows.length) { toast.error('No data rows found in the CSV'); return; }
    setValidating(true); setValidateResult(null);
    try {
      const payload = new FormData();
      payload.append('file', new File([new Blob([buildRemappedCSV()], { type: 'text/csv' })], 'import.csv', { type: 'text/csv' }));
      const res = await api.post('/students/bulk-upload?dryRun=true', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
      setValidateResult(res.data);
      setImportStep('validate');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validation failed');
    } finally { setValidating(false); }
  };

  const handleImportUpload = async () => {
    if (!csvRows.length) { toast.error('No data rows found in the CSV'); return; }
    setImporting(true); setImportResult(null);
    try {
      const payload = new FormData();
      payload.append('file', new File([new Blob([buildRemappedCSV()], { type: 'text/csv' })], 'import.csv', { type: 'text/csv' }));
      const res = await api.post('/students/bulk-upload', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      toast.success(res.data.message);
      invalidateStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  // ── Derived ──────────────────────────────────────────────────────────────

  const schemaFields = applyFieldSettings(fieldSettings);
  const existingFamilyIds = [...new Set(students.map(s => s.familyId).filter(Boolean))];
  const selectedRoute = transportRoutes.find(r => r.id === formData.routeId);
  const pickupPoints = selectedRoute?.pickupPoints || [];

  const COLORS = [
    'bg-indigo-600/90 text-white','bg-amber-600/90 text-white',
    'bg-teal-600/90 text-white','bg-pink-600/90 text-white',
    'bg-rose-600/90 text-white','bg-cyan-600/90 text-white','bg-emerald-600/90 text-white',
  ];

  // ── Modal title ──────────────────────────────────────────────────────────

  const modalTitle = selectedStudent
    ? 'Edit Student'
    : admissionStep === 'check'
    ? 'New Student Admission'
    : admissionStep === 'search'
    ? 'Find Family'
    : linkedFamily
    ? `New Student — Sibling of ${linkedFamily.siblingName}`
    : 'Add New Student';

  // ── Loading ──────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
    </div>
  );

  return (
    <div className="flex flex-col gap-4" style={{ height: 'calc(100vh - 126px)' }}>

      {/* Page Header */}
      <div className="flex-shrink-0 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          {/* <h2 className="font-outfit font-semibold text-2xl text-slate-800">Students</h2> */}
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${selectedSession === 'all' ? 'bg-slate-100 text-slate-600' : 'bg-emerald-50 text-emerald-700'}`}>
              Session: {selectedSession === 'all' ? 'All Sessions' : selectedSession}
            </span>
            <div className="h-4 w-px bg-slate-200" />
            <select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setPage(1); }}
              className="bg-transparent border-none text-xs font-semibold text-slate-500 focus:ring-0 cursor-pointer">
              <option value="all">All Classes</option>
              {classes.filter(c => selectedSession === 'all' || c.sessionYear === selectedSession).map(c => (
                <option key={c.id} value={c.id}>{c.className}</option>
              ))}
            </select>
            <div className="h-4 w-px bg-slate-200" />
            <span className="text-[11px] text-slate-400">{totalStudents} students</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search name, admission, phone…"
              className="pl-9 pr-4 py-2.5 rounded-full border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none w-64 transition-all"
            />
          </div>
          <button onClick={() => { setShowImportModal(true); setImportResult(null); setImportFile(null); setImportStep('upload'); setCsvHeaders([]); setCsvRows([]); setColumnMapping({}); }}
            className="border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-full px-4 py-2.5 font-medium text-sm transition-all flex items-center gap-2">
            <Upload className="w-4 h-4" /> Import CSV
          </button>
          <button onClick={openAddModal}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-2.5 font-medium shadow-sm transition-all flex items-center gap-2">
            <Plus className="w-5 h-5" /> Add Student
          </button>
        </div>
      </div>

      {/* Table — fills all space between header and pagination */}
      {(() => {
        const visibleCols = columnSettings.filter(c => c.visible);
        const tableHeaders = [...visibleCols.map(c => COLUMN_HEADERS[c.key]), 'Actions'];
        const colSpan = tableHeaders.length;
        return (
          <div className="flex-1 min-h-0 relative">
            {fetching && (
              <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-white/60 backdrop-blur-[2px]">
                <div className="flex items-center gap-2.5 px-4 py-2.5 bg-white rounded-full shadow-md border border-slate-200/60">
                  <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs font-semibold text-slate-600">Loading…</span>
                </div>
              </div>
            )}
            <CommonTable scrollable headers={tableHeaders}>
              {filteredStudents.length > 0 ? filteredStudents.map((student, index) => {
                const cls   = classes.find(c => c.id === student.classId);
                const sec   = sections.find(s => s.id === student.sectionId);
                const route = transportRoutes.find(r => r.id === student.routeId);
                return (
                  <tr key={student.id} className="hover:bg-indigo-50/40 transition-colors cursor-pointer" onClick={() => navigate(`/admin/students/${student.id}`)}>
                    {visibleCols.map(col => {
                      switch (col.key) {
                        case 'student': return (
                          <td key="student" className="px-6 py-4">
                            <TableRowAvatarCell title={student.name} subtitle={student.admissionNumber || ''}
                              colorClass={COLORS[index % COLORS.length]} />
                          </td>
                        );
                        case 'admissionNo': return (
                          <td key="admissionNo" className="px-6 py-4 font-mono text-slate-600 text-xs">{student.admissionNumber || '—'}</td>
                        );
                        case 'rollNo': return (
                          <td key="rollNo" className="px-6 py-4 text-slate-600 font-semibold text-sm">{student.rollNumber || '—'}</td>
                        );
                        case 'classSec': return (
                          <td key="classSec" className="px-6 py-4">
                            <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-full">
                              {cls?.className || 'N/A'} - {sec?.sectionName || 'N/A'}
                            </span>
                          </td>
                        );
                        case 'dateOfBirth': return (
                          <td key="dateOfBirth" className="px-6 py-4 text-slate-600 text-sm">{student.dateOfBirth || '—'}</td>
                        );
                        case 'fatherName': return (
                          <td key="fatherName" className="px-6 py-4 text-slate-700 text-sm font-medium">{student.fatherName || '—'}</td>
                        );
                        case 'fatherPhone': return (
                          <td key="fatherPhone" className="px-6 py-4 font-mono text-slate-600 text-xs">{student.fatherPhone || '—'}</td>
                        );
                        case 'motherName': return (
                          <td key="motherName" className="px-6 py-4 text-slate-700 text-sm font-medium">{student.motherName || '—'}</td>
                        );
                        case 'motherPhone': return (
                          <td key="motherPhone" className="px-6 py-4 font-mono text-slate-600 text-xs">{student.motherPhone || '—'}</td>
                        );
                        case 'aadharNumber': return (
                          <td key="aadharNumber" className="px-6 py-4 font-mono text-slate-600 text-xs">{student.aadharNumber || '—'}</td>
                        );
                        case 'address': return (
                          <td key="address" className="px-6 py-4 text-slate-500 text-xs max-w-[160px] truncate">{student.address || '—'}</td>
                        );
                        case 'familyId': return (
                          <td key="familyId" className="px-6 py-4">
                            {student.familyId ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 text-[10px] font-bold rounded-full border border-purple-200/40">
                                <Users className="w-3 h-3" />{student.familyId}
                                {student.siblingDiscountApplied && <span className="ml-0.5 text-emerald-600">✦</span>}
                              </span>
                            ) : <span className="text-[10px] text-slate-300">—</span>}
                          </td>
                        );
                        case 'transport': return (
                          <td key="transport" className="px-6 py-4">
                            {student.useTransport ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200/40">
                                <Truck className="w-3 h-3" />{route?.name || 'Transport'}
                              </span>
                            ) : <span className="text-[10px] text-slate-300">—</span>}
                          </td>
                        );
                        case 'credentials': return (
                          <td key="credentials" className="px-6 py-4">
                            <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-1 rounded font-mono select-all">{student.loginId}</span>
                          </td>
                        );
                        default: return null;
                      }
                    })}
                    <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEditModal(student)} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(student.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan={colSpan} className="text-center py-12 text-slate-500">No students found</td></tr>
              )}
            </CommonTable>
          </div>
        );
      })()}

      {/* Pagination — always pinned below table */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-between px-1">
          <p className="text-xs text-slate-400">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalStudents)} of {totalStudents}
          </p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
              let p;
              if (totalPages <= 7) p = i + 1;
              else if (page <= 4) p = i + 1;
              else if (page >= totalPages - 3) p = totalPages - 6 + i;
              else p = page - 3 + i;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${page === p ? 'bg-indigo-600 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Add / Edit Student Modal ── */}
      <RightSlideModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalTitle}
        maxWidth="max-w-xl"
      >
        {/* ── Step: Sibling Check ── */}
        {admissionStep === 'check' && (
          <SiblingCheckStep onNo={handleNewFamily} onYes={handleFindSibling} />
        )}

        {/* ── Step: Search Sibling ── */}
        {admissionStep === 'search' && (
          <SearchSiblingStep
            students={students}
            classes={classes}
            sections={sections}
            onBack={() => setAdmissionStep('check')}
            onSelect={handleSiblingSelected}
          />
        )}

        {/* ── Step: Form ── */}
        {admissionStep === 'form' && (
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Family Link Banner */}
            {linkedFamily ? (
              <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                <Link2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-emerald-700">Linked to existing family</p>
                  <p className="text-[11px] text-emerald-600 truncate">
                    <span className="font-mono font-bold">{linkedFamily.familyId}</span>
                    {' · '}Sibling of {linkedFamily.siblingName}
                    {' · '}Discount assigned by workflow
                  </p>
                </div>
                {!selectedStudent && (
                  <button type="button" onClick={() => setAdmissionStep('check')}
                    className="text-emerald-400 hover:text-emerald-600 shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ) : formData.familyId && !selectedStudent ? (
              <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-xl">
                <Sparkles className="w-4 h-4 text-indigo-500 shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-bold text-indigo-700">New Family Created</p>
                  <p className="text-[11px] text-indigo-600">Family code <span className="font-mono font-black">{formData.familyId}</span> auto-generated</p>
                </div>
                <button type="button" onClick={() => setAdmissionStep('check')}
                  className="text-indigo-400 hover:text-indigo-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : null}

            {/* Name + Roll */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Roll Number
                  <span className={`ml-1 font-normal text-xs ${fieldSettings.rollNumber ? 'text-rose-500' : 'text-slate-400'}`}>
                    {fieldSettings.rollNumber ? '(required)' : '(optional)'}
                  </span>
                </label>
                <input type="text" value={formData.rollNumber} onChange={e => setFormData({ ...formData, rollNumber: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
                  required={fieldSettings.rollNumber} />
              </div>
            </div>

            {/* Session / Class / Section */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Session</label>
                <select value={modalSession} onChange={e => { setModalSession(e.target.value); setFormData({ ...formData, classId: '', sectionId: '' }); }}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white text-xs outline-none" required>
                  <option value="">Session</option>
                  {sessions.map(s => <option key={s.id} value={s.sessionYear}>{s.sessionYear}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Class</label>
                <select value={formData.classId} onChange={e => { setFormData({ ...formData, classId: e.target.value, sectionId: '' }); fetchSectionsForForm(e.target.value); }}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white text-xs outline-none" required disabled={!modalSession}>
                  <option value="">Class</option>
                  {classes.filter(c => c.sessionYear === modalSession).map(c => <option key={c.id} value={c.id}>{c.className}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Section</label>
                <select value={formData.sectionId} onChange={e => setFormData({ ...formData, sectionId: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white text-xs outline-none" required disabled={!formData.classId}>
                  <option value="">Section</option>
                  {availableSections.map(s => <option key={s.id} value={s.id}>{s.sectionName}</option>)}
                </select>
              </div>
            </div>

            {/* Admission / DOB */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Admission Number
                  {!fieldSettings.admissionNumber && <span className="ml-1 text-slate-400 font-normal text-xs">(optional)</span>}
                </label>
                <input type="text" value={formData.admissionNumber} onChange={e => setFormData({ ...formData, admissionNumber: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
                  required={fieldSettings.admissionNumber} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Date of Birth
                  {!fieldSettings.dateOfBirth && <span className="ml-1 text-slate-400 font-normal text-xs">(optional)</span>}
                </label>
                <input type="date" value={formData.dateOfBirth} onChange={e => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
                  required={fieldSettings.dateOfBirth} />
              </div>
            </div>

            {/* Father Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Father Name
                  {linkedFamily && <span className="ml-1 text-[10px] text-emerald-600 font-normal">↩ pre-filled</span>}
                  {!fieldSettings.fatherName && <span className="ml-1 text-slate-400 font-normal text-xs">(optional)</span>}
                </label>
                <input type="text" value={formData.fatherName} onChange={e => setFormData({ ...formData, fatherName: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
                  required={fieldSettings.fatherName} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Father Phone
                  {linkedFamily && <span className="ml-1 text-[10px] text-emerald-600 font-normal">↩ pre-filled</span>}
                  {!fieldSettings.fatherPhone && <span className="ml-1 text-slate-400 font-normal text-xs">(optional)</span>}
                </label>
                <input type="tel" value={formData.fatherPhone} onChange={e => setFormData({ ...formData, fatherPhone: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
                  required={fieldSettings.fatherPhone} />
              </div>
            </div>

            {/* Mother Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Mother Name
                  {linkedFamily && <span className="ml-1 text-[10px] text-emerald-600 font-normal">↩ pre-filled</span>}
                  {fieldSettings.motherName
                    ? <span className="ml-1 text-rose-500 font-normal text-xs">(required)</span>
                    : <span className="ml-1 text-slate-400 font-normal text-xs">(optional)</span>}
                </label>
                <input type="text" value={formData.motherName} onChange={e => setFormData({ ...formData, motherName: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
                  required={fieldSettings.motherName} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Mother Phone
                  {fieldSettings.motherPhone
                    ? <span className="ml-1 text-rose-500 font-normal text-xs">(required)</span>
                    : <span className="ml-1 text-slate-400 font-normal text-xs">(optional)</span>}
                </label>
                <input type="tel" value={formData.motherPhone} onChange={e => setFormData({ ...formData, motherPhone: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
                  required={fieldSettings.motherPhone} />
              </div>
            </div>

            {/* Aadhar Number */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Aadhar Number
                {fieldSettings.aadharNumber
                  ? <span className="ml-1 text-rose-500 font-normal text-xs">(required)</span>
                  : <span className="ml-1 text-slate-400 font-normal text-xs">(optional)</span>}
              </label>
              <input type="text" maxLength={12} value={formData.aadharNumber}
                onChange={e => setFormData({ ...formData, aadharNumber: e.target.value.replace(/\D/g, '') })}
                placeholder="12-digit Aadhar number"
                className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
                required={fieldSettings.aadharNumber} />
            </div>

            {/* Family ID + Scholarship */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Family ID
                  <span className={`ml-1 font-normal text-xs ${fieldSettings.familyId ? 'text-rose-500' : 'text-slate-400'}`}>
                    {fieldSettings.familyId ? '(required)' : '(optional)'}
                  </span>
                </label>
                <input type="text" value={formData.familyId} onChange={e => setFormData({ ...formData, familyId: e.target.value })}
                  list="family-id-list" placeholder="e.g. FAM-1001"
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
                  required={fieldSettings.familyId} />
                <datalist id="family-id-list">{existingFamilyIds.map(f => <option key={f} value={f} />)}</datalist>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Scholarship %</label>
                <input type="number" min="0" max="100" value={formData.scholarshipPercent}
                  onChange={e => setFormData({ ...formData, scholarshipPercent: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all" />
              </div>
            </div>

            {/* Transport */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden">
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm font-medium text-slate-700">School Transport</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <span className="text-xs text-slate-500">{formData.useTransport ? 'Enabled' : 'Disabled'}</span>
                  <div className="relative">
                    <input type="checkbox" className="sr-only peer" checked={formData.useTransport}
                      onChange={e => setFormData({ ...formData, useTransport: e.target.checked, routeId: '', pickupPoint: '' })} />
                    <div className="w-10 h-6 bg-slate-200 peer-checked:bg-emerald-500 rounded-full transition-colors" />
                    <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                  </div>
                </label>
              </div>
              {formData.useTransport && (
                <div className="px-3 pb-3 grid grid-cols-2 gap-3 border-t border-slate-200">
                  <div className="pt-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">Route</label>
                    <select value={formData.routeId} onChange={e => setFormData({ ...formData, routeId: e.target.value, pickupPoint: '' })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500">
                      <option value="">Select route</option>
                      {transportRoutes.map(r => <option key={r.id} value={r.id}>{r.name} — ₹{r.monthlyFee}/mo</option>)}
                    </select>
                  </div>
                  <div className="pt-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">Pickup Point</label>
                    <input type="text" value={formData.pickupPoint} onChange={e => setFormData({ ...formData, pickupPoint: e.target.value })}
                      list="pickup-points-list" placeholder="Select or type stop"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500" />
                    <datalist id="pickup-points-list">
                      {pickupPoints.map((pt, i) => <option key={i} value={pt} />)}
                    </datalist>
                  </div>
                </div>
              )}
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Address
                {fieldSettings.address
                  ? <span className="ml-1 text-rose-500 font-normal text-xs">(required)</span>
                  : <span className="ml-1 text-slate-400 font-normal text-xs">(optional)</span>}
              </label>
              <textarea value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all" rows="2"
                required={fieldSettings.address} />
            </div>

            <div className="flex gap-3 pt-2">
              {!selectedStudent && (
                <button type="button" onClick={() => setAdmissionStep('check')}
                  className="flex items-center gap-1.5 px-4 py-2.5 border border-slate-200 hover:bg-slate-50 rounded-full text-sm font-medium text-slate-600 transition-all">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              )}
              <button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-2.5 font-medium transition-all">
                {selectedStudent ? 'Update Student' : 'Add Student'}
              </button>
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2.5 border border-slate-200 hover:bg-slate-50 rounded-full text-sm font-medium text-slate-600 transition-all">
                Cancel
              </button>
            </div>
          </form>
        )}
      </RightSlideModal>

      {/* ── CSV Import Modal ── */}
      <RightSlideModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title={importStep === 'validate' ? 'Validation Results' : importStep === 'map' ? 'Map CSV Columns' : 'Import Students from CSV'}
        maxWidth="max-w-xl"
      >
        {importStep === 'upload' ? (
          <div className="space-y-5">
            <p className="text-xs text-slate-400">Upload any CSV — you'll map its columns to student fields on the next step.</p>

            {/* Download template */}
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Need a template?</p>
              <button onClick={handleDownloadTemplate}
                className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                <Download className="w-4 h-4 text-indigo-600" /> Download Template CSV
              </button>
            </div>

            {/* Upload zone */}
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Upload your CSV</p>
              <div onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30 transition-all">
                <Upload className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-500">Click to select a CSV file</p>
                <p className="text-[11px] text-slate-400 mt-1">Any column names — you'll map them next</p>
              </div>
              <input ref={fileInputRef} type="file" accept=".csv" className="hidden"
                onChange={e => handleFileSelect(e.target.files[0])} />
            </div>
          </div>
        ) : importStep === 'map' ? (
          <div className="space-y-5">
            {/* File + row count summary */}
            <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-100 rounded-xl">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                <Upload className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-indigo-800 truncate">{importFile?.name}</p>
                <p className="text-[11px] text-indigo-500">{csvRows.length} data rows · {csvHeaders.length} columns detected</p>
              </div>
              <button onClick={() => { setImportStep('upload'); setImportFile(null); setImportResult(null); setValidateResult(null); setCsvHeaders([]); setCsvRows([]); setColumnMapping({}); fileInputRef.current && (fileInputRef.current.value = ''); }}
                className="text-indigo-400 hover:text-indigo-600 shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Mapping table */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Column Mapping</p>
                <p className="text-[10px] text-slate-400">Your CSV column → Student field</p>
              </div>
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-2 text-left font-semibold text-slate-500 w-1/3">Student Field</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-500 w-1/3">Your CSV Column</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-500">Preview (row 1)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {schemaFields.map(({ key, label, required, hint }) => {
                      const mapped = columnMapping[key];
                      const preview = mapped && csvRows[0] ? csvRows[0][mapped] : null;
                      return (
                        <tr key={key} className="hover:bg-slate-50/50">
                          <td className="px-3 py-2">
                            <p className="font-semibold text-slate-700">{label}</p>
                            {required
                              ? <span className="text-[10px] text-rose-500 font-bold">Required</span>
                              : <span className="text-[10px] text-slate-400">Optional</span>}
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={mapped || ''}
                              onChange={e => setColumnMapping(m => ({ ...m, [key]: e.target.value || undefined }))}
                              className={`w-full px-2 py-1.5 rounded-lg border text-xs outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-all ${mapped ? 'border-indigo-200 bg-indigo-50 text-indigo-800 font-medium' : 'border-slate-200 bg-white text-slate-400'}`}
                            >
                              <option value="">— not mapped —</option>
                              {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            {preview
                              ? <span className="font-mono text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">{preview}</span>
                              : <span className="text-slate-300 text-[11px] italic">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={handleValidate} disabled={validating}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-full py-2.5 font-medium text-sm flex items-center justify-center gap-2">
                {validating ? 'Validating…' : <><CheckCircle className="w-4 h-4" /> Validate Data</>}
              </button>
              <button onClick={() => setShowImportModal(false)}
                className="px-5 border border-slate-200 hover:bg-slate-50 rounded-full py-2.5 font-medium text-sm text-slate-600">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          /* validate step */
          <div className="space-y-5">
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl p-4 bg-emerald-50 border border-emerald-100 text-center">
                <p className="text-2xl font-bold text-emerald-600">{validateResult?.added ?? 0}</p>
                <p className="text-xs text-emerald-700 mt-1 font-medium">Rows ready to import</p>
              </div>
              <div className="rounded-xl p-4 bg-rose-50 border border-rose-100 text-center">
                <p className="text-2xl font-bold text-rose-500">{validateResult?.errors?.length ?? 0}</p>
                <p className="text-xs text-rose-600 mt-1 font-medium">Rows will be skipped</p>
              </div>
            </div>

            {/* File info pill */}
            <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
              <Upload className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="font-medium text-slate-600 truncate">{importFile?.name}</span>
              <span className="text-slate-400 shrink-0">· {validateResult?.total} total rows</span>
            </div>

            {/* Warning or all-clear */}
            {validateResult?.errors?.length > 0 ? (
              <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Some rows have issues</p>
                  <p className="text-amber-600 mt-0.5">Only valid rows will be imported. Fix the CSV and re-upload to import all rows.</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800">
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                <p className="font-semibold">All rows are valid — ready to import!</p>
              </div>
            )}

            {/* Error list */}
            {validateResult?.errors?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Invalid Rows</p>
                <div className="rounded-xl border border-rose-100 overflow-hidden max-h-52 overflow-y-auto">
                  {validateResult.errors.map((e, i) => (
                    <div key={i} className={`flex items-start gap-3 px-3 py-2 text-xs ${i % 2 === 0 ? 'bg-white' : 'bg-rose-50/40'}`}>
                      <span className="shrink-0 font-bold font-mono text-rose-400 w-14">Row {e.row}</span>
                      <span className="text-slate-600">{e.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Import result (after actual import) */}
            {importResult && (
              <div className={`rounded-xl p-4 border text-sm space-y-1 ${importResult.errors?.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                <div className="flex items-center gap-2 font-semibold">
                  {importResult.errors?.length > 0
                    ? <AlertCircle className="w-4 h-4 text-amber-600" />
                    : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                  {importResult.added} students imported of {importResult.total} rows
                </div>
              </div>
            )}

            <div className="flex gap-3">
              {!importResult ? (
                <>
                  <button onClick={() => setImportStep('map')}
                    className="flex items-center gap-1.5 px-4 py-2.5 border border-slate-200 hover:bg-slate-50 rounded-full text-sm font-medium text-slate-600 transition-all">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  {(validateResult?.added ?? 0) > 0 ? (
                    <button onClick={handleImportUpload} disabled={importing}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-full py-2.5 font-medium text-sm flex items-center justify-center gap-2">
                      {importing ? 'Importing…' : <><CheckCircle className="w-4 h-4" /> Proceed with {validateResult.added} rows</>}
                    </button>
                  ) : (
                    <div className="flex-1 text-center text-sm text-slate-400 py-2.5 italic">No valid rows to import</div>
                  )}
                  <button onClick={() => setShowImportModal(false)}
                    className="px-5 border border-slate-200 hover:bg-slate-50 rounded-full py-2.5 font-medium text-sm text-slate-600">
                    Cancel
                  </button>
                </>
              ) : (
                <button onClick={() => setShowImportModal(false)}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-2.5 font-medium text-sm flex items-center justify-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Done
                </button>
              )}
            </div>
          </div>
        )}
      </RightSlideModal>
    </div>
  );
}
